import { useEffect, useState } from "react";

interface UseExpiryTimerReturn {
  /** Seconds remaining until `deadline`. Clamped to 0. */
  timeLeft: number;
  /** True once the timer reaches 0. Sticky — never goes back to false within a single deadline. */
  isExpired: boolean;
}

const computeTimeLeft = (deadline: Date | null): number => {
  if (!deadline) return 0;
  return Math.max(0, Math.floor((deadline.getTime() - Date.now()) / 1000));
};

/**
 * 1Hz countdown for a Date deadline. Returns the remaining seconds and a
 * sticky isExpired flag once it hits zero.
 *
 * Drives the booking-fee payment window timer for both the self-schedule
 * checkout step and the event-specific /pay/{patientId}/{eventId} page.
 *
 * Resets when `deadline` changes (compared by timestamp, not reference, so
 * callers don't need to memoize the Date).
 */
export const useExpiryTimer = (deadline: Date | null): UseExpiryTimerReturn => {
  const deadlineMs = deadline?.getTime() ?? null;
  const [timeLeft, setTimeLeft] = useState<number>(() =>
    computeTimeLeft(deadline),
  );
  const [isExpired, setIsExpired] = useState<boolean>(
    () => deadline != null && computeTimeLeft(deadline) <= 0,
  );

  useEffect(() => {
    if (deadlineMs == null) {
      setTimeLeft(0);
      setIsExpired(false);
      return;
    }

    const deadlineDate = new Date(deadlineMs);

    // Reset state for the new deadline before starting the interval — `isExpired`
    // must clear when the deadline moves from past to future.
    const initial = computeTimeLeft(deadlineDate);
    setTimeLeft(initial);
    setIsExpired(initial <= 0);

    const tick = () => {
      const remaining = computeTimeLeft(deadlineDate);
      setTimeLeft(remaining);
      if (remaining <= 0) {
        setIsExpired(true);
      }
    };

    const intervalId = setInterval(tick, 1000);
    return () => clearInterval(intervalId);
  }, [deadlineMs]);

  return { timeLeft, isExpired };
};
